import { useState } from "react";
import { LemonCheckout, CheckoutProduct, BillingInterval } from "@/components/checkout/LemonCheckout";
import { DocPackCheckout } from "@/components/checkout/DocPackCheckout";
import { usePageSEO } from "@/hooks/usePageSEO";
import { PRODUCT_FACTS } from "@/config/product";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  FileArchive,
  Layers,
  ShieldCheck,
  X,
  Check,
  ClipboardList,
  Bot,
  Wrench,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Package,
  FileSpreadsheet,
} from "lucide-react";
import { trackClick } from "@/lib/trackClick";
import { PractitionerFeedback } from "@/components/marketing/PractitionerFeedback";
import { FAQSection } from "@/components/marketing/FAQSection";
import aymenPhoto from "@/assets/aymen-bentijani.jpg.asset.json";

export default function Home() {
  const [showDocPackCheckout, setShowDocPackCheckout] = useState(false);
  const [showLemonCheckout, setShowLemonCheckout] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState<CheckoutProduct>('pro');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');

  usePageSEO({
    title: "ISO 27001 Toolkit 2026 - Templates, Risk Register & Audit",
    description: "ISO 27001:2022 toolkit: 40+ editable policy templates, risk register, SoA, gap analysis, and Stage 1 & 2 audit readiness. Start free.",
    canonicalPath: "/",
  });

  const openCheckout = (product: CheckoutProduct, interval: BillingInterval = 'monthly') => {
    setCheckoutProduct(product);
    setBillingInterval(interval);
    setShowLemonCheckout(true);
  };

  const routingCards = [
    {
      icon: FileArchive,
      eyebrow: "Document Pack",
      need: "I need editable ISO 27001 documents",
      desc: `${PRODUCT_FACTS.documentCountLabel} editable policies, procedures, and registers. One-time payment, yours to keep.`,
      cta: "Get the Document Pack",
      onClick: () => { trackClick("home_route_doc_pack", "Get the Document Pack"); setShowDocPackCheckout(true); },
      to: undefined as string | undefined,
      tone: "border-accent/40",
    },
    {
      icon: Layers,
      eyebrow: "Audit Room",
      need: "I need to manage my implementation",
      desc: "Complete workspace. Includes the Stage 1 + Stage 2 readiness checklist, risk register, evidence, owners, due dates, all 11 AI tools, AI Assistant, and Excel/Word exports.",
      cta: "View Audit Room",
      onClick: undefined,
      to: "/audit-room",
      tone: "border-primary/40",
    },
    {
      icon: ShieldCheck,
      eyebrow: "Readiness Check",
      need: "I need to know what is missing",
      desc: "Answer a few questions and get your audit gap list, top priorities, and Stage 1 / Stage 2 summary. Free.",
      cta: "Run the Readiness Check",
      onClick: undefined,
      to: "/readiness-check",
      tone: "border-success/40",
    },
  ];

  const without = [
    "Scattered files across drives and inboxes",
    "Unclear evidence for each control",
    "Weak or spreadsheet-only risk tracking",
    "Last-minute audit panic",
  ];

  const withKit = [
    `${PRODUCT_FACTS.documentCountLabel} editable ISO 27001 documents`,
    "Risk and evidence tracking in one place",
    "Stage 1 and Stage 2 readiness view",
    "Audit-focused implementation workflow",
  ];

  const auditorAsks = [
    { title: "Scope", desc: "What is in and out of your ISMS." },
    { title: "Risk methodology", desc: "How you score and treat risk." },
    { title: "Risk register", desc: "Identified risks, owners, treatments." },
    { title: "Statement of Applicability", desc: "Annex A controls with justification." },
    { title: "Evidence of implemented controls", desc: "Proof each control is operating." },
    { title: "Internal audit", desc: "Plan, findings, and follow-ups." },
    { title: "Management review", desc: "Minutes, decisions, actions." },
    { title: "Corrective actions", desc: "Nonconformities and how you closed them." },
  ];

  const productLayers = [
    { icon: FileArchive, name: "Document Pack", text: "Editable ISO 27001 templates you can adapt and keep." },
    { icon: Wrench, name: `${PRODUCT_FACTS.aiToolCountLabel} AI Tools`, text: "Generate SoA, gap analysis, audit plan, roadmap, and more." },
    { icon: Layers, name: "Audit Room", text: "Complete workspace: Stage 1 + Stage 2 checklist, risks, evidence, owners, due dates, 11 AI tools, AI Assistant, Excel/Word exports." },
    { icon: Bot, name: "AI Assistant", text: "Ask ISO 27001 questions and get answers grounded in the standard." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <LemonCheckout open={showLemonCheckout} onClose={() => setShowLemonCheckout(false)} product={checkoutProduct} interval={billingInterval} />
      <DocPackCheckout open={showDocPackCheckout} onClose={() => setShowDocPackCheckout(false)} />

      {/* Hero - buyer routing */}
      <section className="relative overflow-hidden bg-background">
        <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-150px] right-[-50px] w-[400px] h-[400px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="container mx-auto px-4 pt-14 pb-10 sm:pt-20 sm:pb-14 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6 bg-accent/15 text-accent border-accent/30 hover:bg-accent/20 text-xs px-3 py-1.5">
              <ShieldCheck className="w-3 h-3 mr-1.5" /> ISO 27001 implementation toolkit
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-foreground mb-5">
              ISO 27001 certification, <span className="text-accent">without drowning in documents.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
              Start with editable ISO 27001 templates. Upgrade to Audit Room when you need risk tracking, evidence management, owners, actions, and Stage 1 / Stage 2 preparation.
            </p>
            <div className="flex flex-col gap-3 justify-center items-center max-w-md mx-auto mb-5">
              <Button
                asChild
                size="lg"
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-14 px-8 text-base sm:text-lg shadow-[0_4px_24px_hsl(var(--accent)/0.3)]"
              >
                <Link to="/iso-27001-templates" onClick={() => trackClick("home_hero_get_kit", "Get the ISO 27001 KIT and Templates")}>
                  <Package className="w-5 h-5 mr-2 shrink-0" />
                  Get the ISO 27001 KIT and Templates
                  <ArrowRight className="w-5 h-5 ml-2 shrink-0" />
                </Link>
              </Button>
              <span className="text-[11px] text-muted-foreground">
                One-time purchase . {PRODUCT_FACTS.documentCountLabel} editable documents
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold h-12 px-6">
                <Link to="/readiness-check" onClick={() => trackClick("home_hero_primary", "Run the Audit Readiness Check")}>
                  Run the Audit Readiness Check <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="font-semibold h-12 px-6">
                <Link to="/pricing" onClick={() => trackClick("home_hero_secondary", "View pricing")}>
                  View pricing
                </Link>
              </Button>
            </div>

            {/* Visual demo of the Document Pack - a policy preview and the Excel register */}
            <div className="mt-12 max-w-3xl mx-auto text-left">
              <div className="flex items-center justify-between mb-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                <span>What's inside the document pack</span>
                <span>Sample view</span>
              </div>
              <div className="bg-card border border-border rounded-2xl shadow-[0_20px_60px_-20px_hsl(var(--accent)/0.25)] overflow-hidden">
                {/* Window chrome */}
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-muted/40">
                  <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
                  <span className="h-2.5 w-2.5 rounded-full bg-warning/70" />
                  <span className="h-2.5 w-2.5 rounded-full bg-success/70" />
                  <span className="ml-3 text-[11px] text-muted-foreground font-mono truncate">iso27001kit.com / documents</span>
                </div>
                {/* Document pack highlights */}
                <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
                  {[
                    { label: "40+ documents", value: "Policies & registers", icon: FileText, tone: "text-accent" },
                    { label: "Word format", value: "Editable .docx files", icon: FileText, tone: "text-primary" },
                    { label: "Excel registers", value: "Risk + SoA workbooks", icon: FileSpreadsheet, tone: "text-success" },
                  ].map((m) => {
                    const Icon = m.icon;
                    return (
                      <div key={m.label} className="px-4 py-3 flex items-center gap-3">
                        <Icon className={`h-4 w-4 ${m.tone}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-foreground leading-tight truncate">{m.label}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{m.value}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Side-by-side preview: most visited policy + Excel register */}
                <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
                  {/* Policy preview */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Information Security Policy</span>
                    </div>
                    <div className="rounded border border-border bg-background p-4 space-y-2 shadow-sm">
                      <p className="text-center text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Information Security Policy</p>
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-foreground">1. Purpose</p>
                        <p className="text-[10px] text-muted-foreground leading-snug">This policy defines the management direction for information security in accordance with ISO 27001:2022...</p>
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-foreground">2. Scope</p>
                        <p className="text-[10px] text-muted-foreground leading-snug">This policy applies to all employees, contractors, and third parties who access the organization's information assets.</p>
                      </div>
                      <div className="flex flex-wrap gap-1 pt-1">
                        {["Approved", "ISO 27001:2022", "Mandatory"].map((tag) => (
                          <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Excel register preview */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileSpreadsheet className="h-4 w-4 text-success" />
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Risk Register</span>
                    </div>
                    <div className="rounded border border-border bg-background overflow-hidden shadow-sm">
                      <table className="w-full text-[10px]">
                        <thead className="bg-success/10">
                          <tr>
                            {["Risk", "Owner", "Score", "Status"].map((h) => (
                              <th key={h} className="px-2 py-1.5 text-left font-semibold text-foreground border-b border-border">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { risk: "Data breach", owner: "CISO", score: "12", status: "Open" },
                            { risk: "Cloud misconfig", owner: "IT Mgr", score: "9", status: "Open" },
                            { risk: "Insider threat", owner: "HR", score: "6", status: "Closed" },
                          ].map((row, i) => (
                            <tr key={i} className="border-b border-border last:border-0">
                              <td className="px-2 py-1.5 text-foreground truncate max-w-[90px]">{row.risk}</td>
                              <td className="px-2 py-1.5 text-muted-foreground">{row.owner}</td>
                              <td className="px-2 py-1.5 font-medium text-foreground">{row.score}</td>
                              <td className="px-2 py-1.5">
                                <span className={`text-[9px] px-1.5 py-0.5 rounded ${row.status === "Closed" ? "bg-success/15 text-success" : "bg-warning/15 text-warning"}`}>{row.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3 routing cards */}
      <section aria-labelledby="route-heading" className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-10">
            <h2 id="route-heading" className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Where do you want to start?
            </h2>
            <p className="text-muted-foreground text-sm">Pick the path that matches what you need today.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 items-stretch">
            {routingCards.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.eyebrow} className={`bg-card border rounded-2xl p-6 flex flex-col ${c.tone} hover:shadow-[0_0_30px_hsl(var(--accent)/0.08)] transition-all`}>
                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-foreground" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-accent font-semibold mb-1">{c.eyebrow}</p>
                  <h3 className="text-lg font-bold text-foreground mb-2 leading-snug">{c.need}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{c.desc}</p>
                  {c.to ? (
                    <Button asChild className="mt-auto bg-accent text-accent-foreground hover:bg-accent/90">
                      <Link to={c.to} onClick={() => trackClick(`home_route_${c.eyebrow}`, c.cta)}>
                        {c.cta} <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  ) : (
                    <Button onClick={c.onClick} className="mt-auto bg-accent text-accent-foreground hover:bg-accent/90">
                      {c.cta} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What you get - clarify Documents vs AI Tools vs Audit Room */}
      <section aria-labelledby="layers-heading" className="py-16 bg-muted/20 border-y border-border">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-10">
            <h2 id="layers-heading" className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Documents, AI tools, and Audit Room - what does what
            </h2>
            <p className="text-muted-foreground text-sm max-w-2xl mx-auto">
              Three layers that work together. Buy only what you need, when you need it.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {productLayers.map((l) => {
              const Icon = l.icon;
              return (
                <div key={l.name} className="bg-card border border-border rounded-xl p-5">
                  <Icon className="h-5 w-5 text-accent mb-3" />
                  <h3 className="text-sm font-semibold text-foreground mb-1">{l.name}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{l.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section aria-labelledby="compare-heading" className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <h2 id="compare-heading" className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              With or without ISO27001KIT
            </h2>
            <p className="text-muted-foreground text-sm">A clearer path to your certification audit.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-card border border-destructive/30 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <X className="h-5 w-5 text-destructive" /> Without ISO27001KIT
              </h3>
              <ul className="space-y-3">
                {without.map((w) => (
                  <li key={w} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <X className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card border border-success/40 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Check className="h-5 w-5 text-success" /> With ISO27001KIT
              </h3>
              <ul className="space-y-3">
                {withKit.map((w) => (
                  <li key={w} className="flex items-start gap-2 text-sm text-foreground">
                    <Check className="h-4 w-4 text-success mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What your auditor will ask for */}
      <section aria-labelledby="auditor-heading" className="py-16 sm:py-20 bg-muted/20 border-y border-border">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-primary/10 text-primary border-primary/30">
              <ClipboardList className="w-3 h-3 mr-1.5" /> Audit-ready by design
            </Badge>
            <h2 id="auditor-heading" className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              What your auditor will ask for
            </h2>
            <p className="text-muted-foreground text-sm max-w-2xl mx-auto">
              Every item below is covered by the Document Pack and Audit Room.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {auditorAsks.map((a) => (
              <div key={a.title} className="bg-card border border-border rounded-xl p-5">
                <Check className="h-4 w-4 text-success mb-2" />
                <h3 className="text-sm font-semibold text-foreground mb-1">{a.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <PractitionerFeedback
        heading="What ISO 27001 and GRC practitioners are saying"
        keys={["nikhil", "felicitas", "david"]}
        compact
      />

      {/* Founder credibility */}
      <section aria-labelledby="founder-heading" className="py-10 sm:py-14 bg-muted/20 border-y border-border">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-card border border-border rounded-2xl p-5 sm:p-7 flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start text-center sm:text-left">
            <img
              src={aymenPhoto.url}
              alt="Aymen Bentijani, Cybersecurity GRC Consultant and Certified ISO/IEC 27001 Lead Implementer"
              loading="lazy"
              width={80}
              height={80}
              className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover border border-border shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h2 id="founder-heading" className="text-lg sm:text-xl font-bold text-foreground mb-2 leading-snug">
                Built by an ISO 27001 practitioner
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                ISO27001KIT is built by Aymen Bentijani, a Cybersecurity GRC Consultant and Certified ISO/IEC 27001 Lead Implementer.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                The kit is designed around practical ISO 27001 implementation work: scope, risk assessment, Statement of Applicability, evidence, and audit preparation.
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/about" onClick={() => trackClick("home_founder_about", "Learn more about Aymen")}>
                  Learn more about Aymen <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Find out where you stand in 5 minutes
          </h2>
          <p className="text-muted-foreground text-sm mb-6">
            The free Readiness Check shows you the audit gaps to close before you buy anything.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold h-12 px-6">
              <Link to="/readiness-check" onClick={() => trackClick("home_footer_primary", "Run the Audit Readiness Check")}>
                Run the Audit Readiness Check <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="font-semibold h-12 px-6">
              <Link to="/pricing" onClick={() => trackClick("home_footer_secondary", "View pricing")}>
                View pricing
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Popular ISO 27001 Resources - internal linking for SEO */}
      <section aria-labelledby="popular-resources-heading" className="border-t border-border bg-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 id="popular-resources-heading" className="text-2xl font-bold text-foreground mb-6 text-center">
            Popular ISO 27001 Resources
          </h2>
          <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {[
              { href: "/documents", label: "Documents" },
              { href: "/ai-assistant", label: "AI Assistant" },
              { href: "/risk-management", label: "Risk Management" },
              { href: "/audit-room", label: "Audit Room" },
              { href: "/readiness-check", label: "Readiness Check" },
              { href: "/soa-generator", label: "SoA Generator" },
              { href: "/tools/gap-analysis", label: "Gap Analysis" },
              { href: "/vendor-checker", label: "Vendor Security Checker" },
              { href: "/pricing", label: "Pricing" },
            ].map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  className="block rounded-lg border border-border bg-card px-4 py-3 text-center font-medium text-foreground hover:border-accent hover:text-accent transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ - SEO + FAQPage schema */}
      <FAQSection
        title="ISO 27001 toolkit - frequently asked questions"
        items={[
          {
            q: "What is included in the ISO 27001 toolkit?",
            a: "The ISO27001KIT toolkit includes 40+ editable Word policy templates, a multi-sheet Excel risk register, a full Statement of Applicability with all 93 Annex A controls, internal audit and gap analysis checklists, plus 11 AI-powered tools and an Audit Room workspace for Stage 1 and Stage 2 preparation.",
          },
          {
            q: "Are the ISO 27001 templates aligned with ISO 27001:2022?",
            a: "Yes. Every policy, register, and checklist is aligned with the ISO/IEC 27001:2022 standard, including the updated Annex A control set (93 controls across 4 themes). The Statement of Applicability template lists all 93 controls with applicability and justification columns.",
          },
          {
            q: "Can I use the ISO 27001 toolkit to get certified?",
            a: "The toolkit gives you the documentation, evidence structure, and audit workflow auditors expect, but certification still requires you to implement the controls, collect evidence, run an internal audit, and pass an external Stage 1 and Stage 2 audit by an accredited certification body.",
          },
          {
            q: "How much does the ISO 27001 toolkit cost?",
            a: "The Document Pack is a one-time $99 purchase and includes 40+ editable templates. The Audit Room subscription is $149/month or $999/year and adds the full workspace with risk register, evidence tracker, 11 AI tools, and the ISO 27001 AI Assistant.",
          },
          {
            q: "Do I need technical expertise to use the templates?",
            a: "No. The templates are written in plain business English with placeholders for your organization name, scope, and owners. A free Readiness Check tells you which documents and controls to prioritize before you start customizing.",
          },
          {
            q: "How long does ISO 27001 implementation take with the toolkit?",
            a: "Most small and mid-sized organizations reach audit readiness in 3-6 months using the toolkit, depending on existing controls and team capacity. The Audit Room checklist breaks the work into Stage 1 and Stage 2 milestones so you always know what is next.",
          },
        ]}
      />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [{
            "@type": "Organization",
            "@id": "https://iso27001kit.com/#organization",
            "name": "iso27001kit",
            "url": "https://iso27001kit.com",
            "logo": "https://iso27001kit.com/logo.png",
            "description": "Complete ISO 27001 implementation toolkit with policies, templates, and tools for information security certification.",
            "email": "contact@iso27001kit.com",
          }, {
            "@type": "WebSite",
            "@id": "https://iso27001kit.com/#website",
            "url": "https://iso27001kit.com",
            "name": "ISO 27001 Kit",
            "description": "Complete ISO 27001 Implementation Kit - Templates, Policies, and Tools",
            "publisher": { "@id": "https://iso27001kit.com/#organization" }
          }, {
            "@type": "Product",
            "name": "ISO 27001 Implementation Kit",
            "description": "Complete toolkit for ISO 27001 certification including 40+ policy templates, implementation guides, and interactive tools.",
            "image": "https://iso27001kit.com/og-image.png",
            "brand": { "@type": "Brand", "name": "ISO27001KIT" },
            "offers": [
              { "@type": "Offer", "name": "ISO 27001 Audit Room Monthly", "price": "149", "priceCurrency": "USD", "priceValidUntil": "2026-12-31", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "ISO 27001 Audit Room Yearly", "price": "999", "priceCurrency": "USD", "priceValidUntil": "2026-12-31", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "ISO 27001 Document Pack", "price": "99", "priceCurrency": "USD", "priceValidUntil": "2026-12-31", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "ISO 27001 AI Assistant Plus", "price": "9", "priceCurrency": "USD", "priceValidUntil": "2026-12-31", "availability": "https://schema.org/InStock" },
            ]
          }]
        })
      }} />
    </div>
  );
}
